/*
 * Pulls the real embedded picture(s) out of one slide in a template's source
 * .pptx, positioned as a percentage of the slide. Shared by Slide Templates
 * and Presentation Templates — both hold their own manifest/`.cache` data
 * with no rendered thumbnail (see slideTemplatesApi.ts and
 * presentationTemplatesApi.ts's module headers), so the source .pptx itself
 * (already fetched at insert time — see slideTemplateInsert.ts /
 * presentationTemplateInsert.ts) is the only place a real photo exists. This
 * does the same fetch (cached by each feature's own thumbnail hook) purely to
 * read, never to insert.
 *
 * Positions are computed against the source deck's OWN `<p:sldSz>`, not
 * either feature's manifest-carried dimensions — the two should always agree
 * for a given entry, but reading it back out of the real file already
 * downloaded means this never depends on the manifest carrying that field at
 * all.
 */
import { strFromU8, unzipSync, type Unzipped } from "fflate";

const NS_P = "http://schemas.openxmlformats.org/presentationml/2006/main";
const NS_A = "http://schemas.openxmlformats.org/drawingml/2006/main";
const NS_R = "http://schemas.openxmlformats.org/officeDocument/2006/relationships";

/** Formats a browser can render directly as an <img>. PowerPoint also embeds
 *  EMF/WMF (Windows metafiles) and sometimes TIFF/SVG for charts, SmartArt,
 *  and vector clip-art — those are skipped rather than mis-rendered. */
const EXT_MIME: Readonly<Record<string, string>> = {
  ".png": "image/png",
  ".jpg": "image/jpeg",
  ".jpeg": "image/jpeg",
  ".gif": "image/gif",
  ".webp": "image/webp",
  ".bmp": "image/bmp",
};

export interface ExtractedSlideImage {
  readonly xPct: number;
  readonly yPct: number;
  readonly wPct: number;
  readonly hPct: number;
  readonly dataUrl: string;
}

function u8ToBase64(bytes: Uint8Array): string {
  let binary = "";
  const chunk = 0x8000; // stay under the arg-count limit of String.fromCodePoint
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCodePoint(...bytes.subarray(i, i + chunk));
  }
  return btoa(binary);
}

function parseXml(text: string): Document {
  return new DOMParser().parseFromString(text, "text/xml");
}

function readSlideSizeEmu(zip: Unzipped): { cx: number; cy: number } | null {
  const presFile = zip["ppt/presentation.xml"];
  if (!presFile) return null;
  const sldSz = parseXml(strFromU8(presFile)).getElementsByTagNameNS(NS_P, "sldSz")[0];
  const cx = Number(sldSz?.getAttribute("cx"));
  const cy = Number(sldSz?.getAttribute("cy"));
  if (!Number.isFinite(cx) || !Number.isFinite(cy) || cx <= 0 || cy <= 0) return null;
  return { cx, cy };
}

/** 1-based `slideIndex` (presentation order) -> the slide part's zip path. */
function resolveSlidePath(zip: Unzipped, slideIndex: number): string | null {
  const presFile = zip["ppt/presentation.xml"];
  const relsFile = zip["ppt/_rels/presentation.xml.rels"];
  if (!presFile || !relsFile) return null;

  const sldIds = parseXml(strFromU8(presFile)).getElementsByTagNameNS(NS_P, "sldId");
  const target = sldIds[slideIndex - 1];
  const rId = target?.getAttributeNS(NS_R, "id") ?? target?.getAttribute("r:id");
  if (!rId) return null;

  for (const rel of Array.from(parseXml(strFromU8(relsFile)).getElementsByTagName("Relationship"))) {
    if (rel.getAttribute("Id") === rId) {
      const relTarget = rel.getAttribute("Target");
      return relTarget ? `ppt/${relTarget}` : null;
    }
  }
  return null;
}

function slideRelsPath(slidePath: string): string {
  const fileName = slidePath.split("/").pop() ?? slidePath;
  return slidePath.replace(fileName, `_rels/${fileName}.rels`);
}

/** "../media/image1.png" (from ppt/slides/_rels/slideN.xml.rels) -> "ppt/media/image1.png". */
function resolveRelativeTarget(slidePath: string, target: string): string {
  const slideDir = slidePath.substring(0, slidePath.lastIndexOf("/"));
  if (target.startsWith("../")) {
    const parentDir = slideDir.substring(0, slideDir.lastIndexOf("/"));
    return `${parentDir}/${target.substring(3)}`;
  }
  return target.startsWith("ppt/") ? target : `${slideDir}/${target}`;
}

function resolveRelTarget(relsDoc: Document | null, slidePath: string, rId: string): string | null {
  if (!relsDoc) return null;
  for (const rel of Array.from(relsDoc.getElementsByTagName("Relationship"))) {
    if (rel.getAttribute("Id") === rId) {
      const target = rel.getAttribute("Target");
      return target ? resolveRelativeTarget(slidePath, target) : null;
    }
  }
  return null;
}

function pictureBlipREmbed(pic: Element): string | null {
  const blip = pic.getElementsByTagNameNS(NS_P, "blipFill")[0]?.getElementsByTagNameNS(NS_A, "blip")[0];
  return blip?.getAttributeNS(NS_R, "embed") || blip?.getAttribute("r:embed") || null;
}

interface EmuRect {
  readonly x: number;
  readonly y: number;
  readonly cx: number;
  readonly cy: number;
}

function readRect(xfrm: Element, offTag: string, extTag: string): EmuRect | null {
  const off = xfrm.getElementsByTagNameNS(NS_A, offTag)[0];
  const ext = xfrm.getElementsByTagNameNS(NS_A, extTag)[0];
  const x = Number(off?.getAttribute("x"));
  const y = Number(off?.getAttribute("y"));
  const cx = Number(ext?.getAttribute("cx"));
  const cy = Number(ext?.getAttribute("cy"));
  if (![x, y, cx, cy].every(Number.isFinite) || cx <= 0 || cy <= 0) return null;
  return { x, y, cx, cy };
}

/**
 * An affine map from a shape's LOCAL xfrm coordinates to slide-absolute EMU:
 * absX = offX + localX * scaleX (and same for Y). Composing these across
 * nested `<p:grpSp>` levels is what makes a picture's own `<a:off>`/`<a:ext>`
 * mean something outside its immediate parent — see `descendIntoGroup` below.
 */
interface Ctx {
  readonly offX: number;
  readonly offY: number;
  readonly scaleX: number;
  readonly scaleY: number;
}

const IDENTITY_CTX: Ctx = { offX: 0, offY: 0, scaleX: 1, scaleY: 1 };

function mapRect(local: EmuRect, ctx: Ctx): EmuRect {
  return {
    x: ctx.offX + local.x * ctx.scaleX,
    y: ctx.offY + local.y * ctx.scaleY,
    cx: local.cx * ctx.scaleX,
    cy: local.cy * ctx.scaleY,
  };
}

interface PictureHit {
  readonly rId: string;
  readonly rect: EmuRect;
}

/** A `<p:pic>` child: resolve its blip rId + local bounds, map into `ctx`, record it. */
function collectPictureShape(pic: Element, ctx: Ctx, out: PictureHit[]): void {
  const rId = pictureBlipREmbed(pic);
  const xfrm = pic.getElementsByTagNameNS(NS_P, "spPr")[0]?.getElementsByTagNameNS(NS_A, "xfrm")[0];
  const local = xfrm && readRect(xfrm, "off", "ext");
  if (rId && local) out.push({ rId, rect: mapRect(local, ctx) });
}

/**
 * The `Ctx` a `<p:grpSp>`'s OWN xfrm implies for its children: `chOff`/`chExt`
 * define the local coordinate space those children are placed in. OOXML lets
 * them be omitted, defaulting to the group's own off/ext (i.e. its children's
 * local space equals its own) — hence the `?? own` fallback.
 */
function groupChildCtx(xfrm: Element, own: EmuRect, absOwn: EmuRect): Ctx {
  const childSpace = readRect(xfrm, "chOff", "chExt") ?? own;
  return {
    offX: absOwn.x - childSpace.x * (absOwn.cx / childSpace.cx),
    offY: absOwn.y - childSpace.y * (absOwn.cy / childSpace.cy),
    scaleX: absOwn.cx / childSpace.cx,
    scaleY: absOwn.cy / childSpace.cy,
  };
}

/** A `<p:grpSp>` child: compose its own transform onto `ctx`, then recurse into it. */
function descendIntoGroup(group: Element, ctx: Ctx, out: PictureHit[]): void {
  const xfrm = group.getElementsByTagNameNS(NS_P, "grpSpPr")[0]?.getElementsByTagNameNS(NS_A, "xfrm")[0];
  const own = xfrm && readRect(xfrm, "off", "ext");
  if (!xfrm || !own) return;
  collectPictures(group, groupChildCtx(xfrm, own, mapRect(own, ctx)), out);
}

/**
 * Walks one level of the shape tree (`<p:spTree>`, or a `<p:grpSp>`'s own
 * children), collecting every `<p:pic>`'s blip rId + slide-absolute bounds.
 *
 * A GROUPED picture's `<a:off>`/`<a:ext>` are expressed in its group's own
 * local coordinate space (bounded by the group's `<a:chOff>`/`<a:chExt>`),
 * not the slide's — reading them as absolute EMU (as a flat
 * `getElementsByTagNameNS(NS_P, "pic")` scan would) puts every grouped photo
 * in the wrong place, often squeezed into a tiny corner. Recursing with a
 * composed `Ctx` per nesting level (via `descendIntoGroup`) is what keeps
 * each picture's position correct regardless of how deep it's grouped.
 */
function collectPictures(container: Element, ctx: Ctx, out: PictureHit[]): void {
  for (const child of Array.from(container.children)) {
    if (child.namespaceURI !== NS_P) continue;
    if (child.localName === "pic") collectPictureShape(child, ctx, out);
    else if (child.localName === "grpSp") descendIntoGroup(child, ctx, out);
  }
}

/**
 * Every real embedded picture on one slide of a source .pptx, as a percentage
 * of that slide's own size — ready to lay directly over a wireframe's
 * placeholder blocks (same 0–100 coordinate space; see SlideTemplatesPanel.tsx
 * / PresentationTemplatesPanel.tsx's LayoutWireframe). Returns [] rather than
 * throwing for anything unreadable — a card falling back to its wireframe is
 * fine, a crash is not.
 */
export function extractSlideImages(pptxBuffer: ArrayBuffer, slideIndex: number): ExtractedSlideImage[] {
  let zip: Unzipped;
  try {
    zip = unzipSync(new Uint8Array(pptxBuffer));
  } catch {
    return [];
  }

  const slideSize = readSlideSizeEmu(zip);
  const slidePath = resolveSlidePath(zip, slideIndex);
  if (!slideSize || !slidePath) return [];

  const slideFile = zip[slidePath];
  if (!slideFile) return [];

  const relsFile = zip[slideRelsPath(slidePath)];
  const relsDoc = relsFile ? parseXml(strFromU8(relsFile)) : null;
  const slideDoc = parseXml(strFromU8(slideFile));
  const spTree = slideDoc.getElementsByTagNameNS(NS_P, "spTree")[0];
  if (!spTree) return [];

  const pictures: PictureHit[] = [];
  collectPictures(spTree, IDENTITY_CTX, pictures);

  const images: ExtractedSlideImage[] = [];
  for (const { rId, rect: bounds } of pictures) {
    const imagePath = resolveRelTarget(relsDoc, slidePath, rId);
    const imageFile = imagePath ? zip[imagePath] : undefined;
    if (!imagePath || !imageFile) continue;

    const ext = imagePath.substring(imagePath.lastIndexOf(".")).toLowerCase();
    const mimeType = EXT_MIME[ext];
    if (!mimeType) continue;

    images.push({
      xPct: (bounds.x / slideSize.cx) * 100,
      yPct: (bounds.y / slideSize.cy) * 100,
      wPct: (bounds.cx / slideSize.cx) * 100,
      hPct: (bounds.cy / slideSize.cy) * 100,
      dataUrl: `data:${mimeType};base64,${u8ToBase64(imageFile)}`,
    });
  }
  return images;
}
